import { DB_COST_LICENSING_NOTE, RDS_COST_PENDING_NOTE, type PoolTierId } from '@/data/sizeCatalog'
import type { VmSizeCheck } from '@/types/infrastructure'
import { formatCurrency } from '@/utils/format'
import { sizingCost } from '@/utils/sizing'
import { sizeOfTier } from '@/utils/vmSizes'
import page from '@/pages/NewDeploymentPage.module.css'

interface SizingCostSummaryProps {
  poolTier: PoolTierId
  /** True when a new SQL VM is built, which adds the DB VM line. */
  newSqlVm: boolean
  /** The VM size each new server is built with in the chosen region. A line shows that SKU, which is not the catalog's where it was replaced. */
  vmSizes?: readonly VmSizeCheck[]
}

/**
 * The RDS VM and, when a new SQL VM is built, the DB VM: one line each, then the total. The RDS VM's price has not been set, so its line says
 * so instead of a number, and the total says it is not known: a partial sum would be read as the whole. The DB line leaves out SQL licensing.
 */
export function SizingCostSummary({ poolTier, newSqlVm, vmSizes }: SizingCostSummaryProps) {
  const cost = sizingCost(poolTier, newSqlVm)
  const substituted = cost.lines.some((line) => sizeOfTier(vmSizes, line.tier)?.status === 'substituted')
  return (
    <section aria-label="Estimated monthly cost" data-testid="sizing-cost">
      <div className={page.reviewTable}>
        {cost.lines.map((line) => (
          <div key={line.tier} className={page.reviewRow}>
            <span className={page.reviewLabel}>
              {line.label} · {sizeOfTier(vmSizes, line.tier)?.effective ?? line.sku}
              {line.excludesSqlLicensing && <span> ({DB_COST_LICENSING_NOTE})</span>}
            </span>
            <span className={page.reviewValue}>{line.monthlyUsd === null ? RDS_COST_PENDING_NOTE : `${formatCurrency(line.monthlyUsd)}/month`}</span>
          </div>
        ))}
      </div>
      <div className={page.costSummary}>
        <span className={page.costLabel}>
          Estimated total
          {cost.excludesSqlLicensing && <span> · {DB_COST_LICENSING_NOTE}</span>}
        </span>
        <span className={page.costValue}>{cost.totalUsd === null ? `Not known yet: ${cost.pending.join(', ')} price to be confirmed` : `${formatCurrency(cost.totalUsd)}/month`}</span>
      </div>
      {substituted && (
        <p className={page.fieldHint} data-testid="sizing-cost-substituted">
          The estimate is for the catalog sizes. A substituted series of the same size may cost slightly differently.
        </p>
      )}
    </section>
  )
}
